import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { CurrentUser, Language, Market, Vendor } from '@bedouin/shared'
import { resources } from '@/api/resources'

/**
 * Markets, vendors and languages are read by nearly every view and change
 * rarely, so they are fetched once per session and shared. `ensureLoaded` is
 * safe to call from many components at once -- concurrent callers await the
 * same in-flight promise rather than each firing their own request.
 */
export const useReferenceStore = defineStore('reference', () => {
  const markets = ref<Market[]>([])
  const vendors = ref<Vendor[]>([])
  const languages = ref<Language[]>([])
  const user = ref<CurrentUser | null>(null)

  const loaded = ref(false)
  const error = ref<string | null>(null)
  let inFlight: Promise<void> | null = null

  async function load(): Promise<void> {
    error.value = null
    try {
      const [marketRows, vendorRows, languageRows, me] = await Promise.all([
        resources.markets.list(),
        resources.vendors.list(),
        resources.languages.list(),
        resources.me(),
      ])
      markets.value = marketRows
      vendors.value = vendorRows
      languages.value = languageRows
      user.value = me
      loaded.value = true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Could not load reference data'
      throw err
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return
    inFlight ??= load().finally(() => {
      inFlight = null
    })
    // A failed load should not be cached as "attempted"; swallow so a view
    // that only wants dropdown options still renders.
    await inFlight.catch(() => undefined)
  }

  /** Force a refetch after editing a vendor or market in settings. */
  async function reload(): Promise<void> {
    loaded.value = false
    await ensureLoaded()
  }

  const marketById = computed(() => new Map(markets.value.map((m) => [m.id, m])))
  const vendorById = computed(() => new Map(vendors.value.map((v) => [v.id, v])))
  const languageByCode = computed(() => new Map(languages.value.map((l) => [l.code, l])))

  const marketOptions = computed(() =>
    markets.value.map((market) => ({ value: market.id, label: `${market.code} - ${market.name}` })),
  )
  const vendorOptions = computed(() =>
    vendors.value.map((vendor) => ({ value: vendor.id, label: vendor.name })),
  )
  const languageOptions = computed(() =>
    languages.value.map((language) => ({
      value: language.code,
      label: `${language.name} (${language.code})`,
    })),
  )

  return {
    markets,
    vendors,
    languages,
    user,
    loaded,
    error,
    ensureLoaded,
    reload,
    marketById,
    vendorById,
    languageByCode,
    marketOptions,
    vendorOptions,
    languageOptions,
  }
})
